// src/features/notifications/hooks/useNotifications.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../../../services/api/client';
import { useAuth } from '../../../context/AuthContext';

// Debug logging utility
const DEBUG_ENABLED = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_NOTIFICATIONS === 'true';

const debugLog = (category, message, data = null) => {
  if (!DEBUG_ENABLED) return;
  
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [useNotifications:${category}]`;
  
  if (data) {
    console.log(prefix, message, data);
  } else {
    console.log(prefix, message);
  }
};

const debugError = (category, message, error = null) => {
  if (!DEBUG_ENABLED) return;
  
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [useNotifications:${category}] ERROR:`;
  
  if (error) {
    console.error(prefix, message, error);
  } else {
    console.error(prefix, message);
  }
};

const debugWarn = (category, message, data = null) => {
  if (!DEBUG_ENABLED) return;
  
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [useNotifications:${category}] WARNING:`;
  
  if (data) {
    console.warn(prefix, message, data);
  } else {
    console.warn(prefix, message);
  }
};

// Simplified singleton manager for v2 polling
class NotificationPollingManager {
  constructor() {
    this.isPolling = false;
    this.subscribers = new Set();
    this.pollingController = null;
    this.pollingTimeoutRef = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.pollTimeoutSeconds = 30;
    this.currentNotificationCount = 0;
    this.currentMessageCount = 0;
    
    debugLog('PollingManager', 'Singleton instance created');
  }

  addSubscriber(hookInstance) {
    this.subscribers.add(hookInstance);
    debugLog('PollingManager', 'Added subscriber', { 
      subscriberCount: this.subscribers.size,
      hookId: hookInstance.id 
    });
    
    // Notify new subscriber of current state
    if (this.isPolling) {
      hookInstance.updatePollingState(
        this.isPolling, 
        this.currentNotificationCount,
        this.currentMessageCount
      );
    }
  }

  removeSubscriber(hookInstance) {
    this.subscribers.delete(hookInstance);
    debugLog('PollingManager', 'Removed subscriber', { 
      subscriberCount: this.subscribers.size,
      hookId: hookInstance.id 
    });
    
    // Stop polling if no subscribers
    if (this.subscribers.size === 0) {
      this.cleanup();
    }
  }

  notifySubscribers(type, data = {}) {
    debugLog('PollingManager', 'Notifying subscribers', { 
      type, 
      subscriberCount: this.subscribers.size,
      data 
    });
    
    this.subscribers.forEach(subscriber => {
      try {
        switch (type) {
          case 'polling_state_changed':
            subscriber.updatePollingState(
              this.isPolling, 
              this.currentNotificationCount,
              this.currentMessageCount
            );
            break;
          case 'notification_count_changed':
            subscriber.updateNotificationCount(data.count);
            break;
          case 'message_count_changed':
            subscriber.updateMessageCount(data.count);
            break;
          default:
            break;
        }
      } catch (error) {
        debugError('PollingManager', 'Error notifying subscriber', error);
      }
    });
  }

  async startPolling(isAuthenticated) {
    if (this.isPolling) {
      debugLog('PollingManager', 'Polling already active');
      return;
    }

    if (!isAuthenticated) {
      debugWarn('PollingManager', 'Cannot start polling - not authenticated');
      return;
    }

    this.isPolling = true;
    
    debugLog('PollingManager', 'Starting polling loop');
    this.pollLoop(isAuthenticated);
  }

  async stopPolling() {
    debugLog('PollingManager', 'Stopping polling', { isPolling: this.isPolling });
    
    this.isPolling = false;
    
    if (this.pollingController) {
      this.pollingController.abort();
      this.pollingController = null;
    }
    
    if (this.pollingTimeoutRef) {
      clearTimeout(this.pollingTimeoutRef);
      this.pollingTimeoutRef = null;
    }
    
    this.notifySubscribers('polling_state_changed');
    debugLog('PollingManager', 'Polling stopped');
  }

  async pollLoop(isAuthenticated) {
    while (this.isPolling && isAuthenticated) {
      try {
        await this.poll();
        
        if (!this.isPolling) break;
        
        // Brief pause between polls
        await new Promise(resolve => {
          this.pollingTimeoutRef = setTimeout(resolve, 30000);
        });
        
      } catch (error) {
        debugError('PollingManager', 'Poll loop error', error);
        
        // Don't break on error - let the retry logic in poll() handle it
        if (!this.isPolling) break;
        
        // Add a longer delay after an error before retrying
        await new Promise(resolve => {
          this.pollingTimeoutRef = setTimeout(resolve, 5000);
        });
      }
    }
    
    debugLog('PollingManager', 'Poll loop ended');
  }

  async poll() {
    if (!this.isPolling) return;

    try {
      if (this.pollingController) {
        this.pollingController.abort();
      }
      
      this.pollingController = new AbortController();
      
      debugLog('PollingManager', 'Making v2 poll request');
      
      const response = await apiClient.get('/api/v2/notifications/poll', {
        params: {
          timeout_seconds: this.pollTimeoutSeconds,
        },
        timeout: (this.pollTimeoutSeconds + 5) * 1000,
        signal: this.pollingController.signal
      });

      const pollData = response.data;
      debugLog('PollingManager', 'Poll response received', {
        notificationCount: pollData.appbar_notification_count,
        messageCount: pollData.unread_message_count,
        pollTimeout: pollData.poll_timeout
      });

      // Update notification count
      const newNotificationCount = pollData.appbar_notification_count || 0;
      if (newNotificationCount !== this.currentNotificationCount) {
        this.currentNotificationCount = newNotificationCount;
        this.notifySubscribers('notification_count_changed', { count: newNotificationCount });
      }

      this.setMessageCount(pollData.unread_message_count || 0);

      this.retryCount = 0; // Reset on success
      
    } catch (error) {
      if (error.name === 'AbortError') {
        debugLog('PollingManager', 'Poll request aborted');
        return;
      }
      
      debugError('PollingManager', 'Poll failed', error);
      
      // Handle 408 timeout (normal behavior)
      if (error.response?.status === 408) {
        return;
      }

      // Increment retry count for other errors
      this.retryCount++;
      if (this.retryCount >= this.maxRetries) {
        debugError('PollingManager', 'Max retries reached, stopping polling');
        this.isPolling = false;
        this.notifySubscribers('polling_state_changed');
        return;
      }
    }
  }

  setMessageCount(count) {
    if (count !== this.currentMessageCount) {
      this.currentMessageCount = count;
      this.notifySubscribers('message_count_changed', { count });
    }
  }

  // Messages update the badge right away after reading/reacting instead of waiting for the next poll
  async refreshMessageCount() {
    try {
      const response = await apiClient.get('/api/v1/messages/unread-count');
      this.setMessageCount(response.data.unread_count || 0);
    } catch (error) {
      debugError('PollingManager', 'Message count refresh failed', error);
    }
  }

  reset() {
    debugLog('PollingManager', 'Resetting notification state');
    this.currentNotificationCount = 0;
    this.currentMessageCount = 0;
    this.notifySubscribers('notification_count_changed', { count: 0 });
    this.notifySubscribers('message_count_changed', { count: 0 });
  }

  cleanup() {
    debugLog('PollingManager', 'Cleaning up polling manager');
    this.stopPolling();
    this.reset();
    this.retryCount = 0;
  }
}

// Create singleton instance
const pollingManager = new NotificationPollingManager();

export function useNotifications() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const hookIdRef = useRef(Math.random().toString(36).substr(2, 9));
  const isUnmountedRef = useRef(false);

  debugLog('init', 'Hook initialized', { hookId: hookIdRef.current, isAuthenticated });

  // Methods called by polling manager
  const updatePollingState = useCallback((newIsPolling, notificationCount, unreadMessageCount) => {
    if (isUnmountedRef.current) return;
    debugLog('hook', 'Updating polling state', { 
      hookId: hookIdRef.current,
      newIsPolling,
      notificationCount,
      unreadMessageCount
    });
    setIsPolling(newIsPolling);
    if (notificationCount !== undefined) {
      setUnreadCount(notificationCount);
    }
    if (unreadMessageCount !== undefined) {
      setMessageCount(unreadMessageCount);
    }
  }, []);

  const updateNotificationCount = useCallback((count) => {
    if (isUnmountedRef.current) return;
    debugLog('hook', 'Updating notification count', { 
      hookId: hookIdRef.current,
      count 
    });
    setUnreadCount(count);
  }, []);

  const updateMessageCount = useCallback((count) => {
    if (isUnmountedRef.current) return;
    debugLog('hook', 'Updating message count', { 
      hookId: hookIdRef.current,
      count 
    });
    setMessageCount(count);
  }, []);

  const hookInstance = {
    id: hookIdRef.current,
    updatePollingState,
    updateNotificationCount,
    updateMessageCount,
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    debugLog('fetch', 'Starting notification fetch', { 
      hookId: hookIdRef.current,
      isAuthenticated 
    });
    
    if (!isAuthenticated || isUnmountedRef.current) return;

    setIsLoading(true);
    
    try {
      const response = await apiClient.get('/api/v1/notifications/', {
        params: { include_read: true, limit: 50 }
      });

      if (isUnmountedRef.current) return;

      const data = response.data;
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
      
      debugLog('fetch', 'Fetch successful', { 
        hookId: hookIdRef.current,
        count: data.notifications?.length || 0 
      });
      
    } catch (error) {
      debugError('fetch', 'Fetch failed', error);
    } finally {
      if (!isUnmountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  const startPolling = useCallback(async () => {
    debugLog('startPolling', 'Start polling requested', { 
      hookId: hookIdRef.current,
      isAuthenticated 
    });
    
    if (!isAuthenticated || isUnmountedRef.current) return;
    
    await pollingManager.startPolling(isAuthenticated);
  }, [isAuthenticated]);

  const stopPolling = useCallback(async () => {
    debugLog('stopPolling', 'Stop polling requested', { hookId: hookIdRef.current });
    await pollingManager.stopPolling();
  }, []);

  const reset = useCallback(() => {
    debugLog('reset', 'Resetting notifications', { hookId: hookIdRef.current });
    pollingManager.reset();
    setNotifications([]);
    setUnreadCount(0);
    setMessageCount(0);
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    if (isUnmountedRef.current) return;
    
    try {
      await apiClient.patch(`/api/v1/notifications/${notificationId}/read`);

      if (isUnmountedRef.current) return;

      setNotifications(prev => 
        prev.map(notification =>
          notification.notification_id === notificationId
            ? { ...notification, is_read: true }
            : notification
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      debugError('markAsRead', 'Failed to mark as read', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unreadNotifications = notifications.filter(n => !n.is_read);
    
    if (isUnmountedRef.current || unreadNotifications.length === 0) return;

    try {
      await Promise.all(
        unreadNotifications.map(notification =>
          apiClient.patch(`/api/v1/notifications/${notification.notification_id}/read`)
        )
      );

      if (isUnmountedRef.current) return;

      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );
      setUnreadCount(0);
      
    } catch (error) {
      debugError('markAllAsRead', 'Failed to mark all as read', error);
    }
  }, [notifications]);

  const getStats = useCallback(async (daysBack = 30) => {
    if (isUnmountedRef.current) return null;
    
    try {
      const response = await apiClient.get('/api/v1/notifications/stats', {
        params: { days_back: daysBack }
      });
      
      return response.data;
    } catch (error) {
      debugError('getStats', 'Failed to get stats', error);
      return null;
    }
  }, []);

  // Register with polling manager
  useEffect(() => {
    isUnmountedRef.current = false;
    pollingManager.addSubscriber(hookInstance);
    
    return () => {
      debugLog('cleanup', 'Hook unmounting', { hookId: hookIdRef.current });
      isUnmountedRef.current = true;
      pollingManager.removeSubscriber(hookInstance);
    };
  }, []);

  // Handle auth changes
  useEffect(() => {
    debugLog('effect:auth', 'Auth state changed', {
      hookId: hookIdRef.current,
      isAuthenticated
    });

    if (isAuthenticated) {
      fetchNotifications();
    } else {
      // User logged out - reset everything
      reset();
      pollingManager.stopPolling();
    }
  }, [isAuthenticated, fetchNotifications, reset]);

  // Auto-dismiss notifications when navigating to the page they link to
  useEffect(() => {
    if (!notifications.length) return;

    const unreadWithUrl = notifications.filter(n => !n.is_read && n.onclick_url);
    if (!unreadWithUrl.length) return;

    const currentPath = location.pathname;

    unreadWithUrl.forEach(notification => {
      let notificationPath;
      try {
        // Handle full URLs (e.g. https://app.example.com/tasks/123)
        const url = new URL(notification.onclick_url, window.location.origin);
        notificationPath = url.pathname;
      } catch {
        notificationPath = notification.onclick_url;
      }

      if (notificationPath === currentPath) {
        debugLog('autoDismiss', 'Auto-marking notification as read for current page', {
          notificationId: notification.notification_id,
          onclick_url: notification.onclick_url,
          currentPath,
        });
        markAsRead(notification.notification_id);
      }
    });
  }, [location.pathname, notifications, markAsRead]);

  const refreshMessageCount = useCallback(() => pollingManager.refreshMessageCount(), []);

  return {
    notifications,
    unreadCount,
    messageCount,
    refreshMessageCount,
    isLoading,
    isPolling,
    fetchNotifications,
    startPolling,
    stopPolling,
    markAsRead,
    markAllAsRead,
    getStats,
    reset,
  };
}